import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DIVA_OUTCOMES,
  IPDE_OUTCOMES,
  fetchDivaReportPdf,
  type DivaScores,
  type ReparseResult,
} from "../services/adhd-assessment";
import {
  useSaveDivaReport,
  useRecordDivaResult,
  useReparseDivaReport,
} from "../hooks/use-adhd-assessment";
import { useToast } from "@/hooks/use-toast";

const SCORE_FIELDS: { key: keyof DivaScores; label: string }[] = [
  { key: "attention_childhood", label: "Inattention — childhood (/9)" },
  { key: "attention_adulthood", label: "Inattention — adulthood (/9)" },
  { key: "hyperactivity_childhood", label: "Hyperactivity — childhood (/9)" },
  { key: "hyperactivity_adulthood", label: "Hyperactivity — adulthood (/9)" },
];

// Combined DIVA report surface: upload the clinician's PDF; once it's saved
// (now or previously), the box expands to show the parsed diagnostic result
// (outcome + symptom totals + impact areas), preloaded and editable. Empty
// only in the no-PDF state.
export function DivaReportDialog({
  usageId,
  reportSaved,
  currentOutcome,
  currentScores,
  reportType,
  trigger,
}: {
  usageId: string;
  /** A report PDF is saved on the usage (metadata.diva_report_key exists). */
  reportSaved?: boolean;
  currentOutcome?: string;
  currentScores?: DivaScores | null;
  /** Report format (bundle care_team_actions.report_type). 'ipde' → yes/no
   *  outcome, no /9 score fields. Default 'diva' → ADHD outcomes + scores. */
  reportType?: string;
  trigger: React.ReactNode;
}) {
  const isIpde = reportType === "ipde";
  const outcomeOptions = isIpde ? IPDE_OUTCOMES : DIVA_OUTCOMES;
  const reportLabel = isIpde ? "IPDE report" : "DIVA report";
  const [open, setOpen] = useState(false);
  // A PDF was uploaded during this open session (so we expand immediately,
  // before the parent's refetch flips `reportSaved`).
  const [savedThisSession, setSavedThisSession] = useState(false);
  const hasReport = !!reportSaved || savedThisSession;

  // New-file selection (upload / replace).
  const [file, setFile] = useState<File | null>(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Saved-PDF preview (streamed blob object URL).
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  // Result fields (expanded section).
  const [outcome, setOutcome] = useState(currentOutcome ?? "");
  const [scores, setScores] = useState<Record<string, string>>({});

  const { mutate: save, isPending: isSaving } = useSaveDivaReport();
  const { mutate: record, isPending: isRecording } = useRecordDivaResult();
  const { mutate: reparse, isPending: isReparsing } = useReparseDivaReport();
  const { toast } = useToast();

  const selectFile = (next: File | null) => {
    if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);
    setFile(next);
    setFilePreviewUrl(next ? URL.createObjectURL(next) : null);
  };
  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  // Preload the result fields from the saved metadata each time the dialog
  // opens, so returning to it shows the last-saved values.
  useEffect(() => {
    if (!open) return;
    setOutcome(currentOutcome ?? "");
    setScores({
      attention_childhood: currentScores?.attention_childhood?.toString() ?? "",
      attention_adulthood: currentScores?.attention_adulthood?.toString() ?? "",
      hyperactivity_childhood:
        currentScores?.hyperactivity_childhood?.toString() ?? "",
      hyperactivity_adulthood:
        currentScores?.hyperactivity_adulthood?.toString() ?? "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      closePreview();
      selectFile(null);
      setSavedThisSession(false);
    }
    setOpen(next);
  };

  const applyParsed = (parsed: ReparseResult["parsed"]) => {
    if (parsed.diva_outcome) setOutcome(parsed.diva_outcome);
    setScores((prev) => {
      const next = { ...prev };
      for (const { key } of SCORE_FIELDS) {
        const v = parsed.diva_scores?.[key];
        if (v !== null && v !== undefined) next[key] = String(v);
      }
      return next;
    });
  };

  // Upload (or replace) the PDF. The server parses on save, so we then reparse
  // to pull the extracted values into the expanded fields.
  const handleUpload = () => {
    if (!file) return;
    save(
      { usage_id: usageId, file },
      {
        onSuccess: () => {
          setSavedThisSession(true);
          selectFile(null);
          reparse(usageId, { onSuccess: ({ parsed }) => applyParsed(parsed) });
        },
      },
    );
  };

  const handleReparse = () =>
    reparse(usageId, { onSuccess: ({ parsed }) => applyParsed(parsed) });

  const togglePreview = async () => {
    if (previewUrl) {
      closePreview();
      return;
    }
    setIsLoadingPreview(true);
    try {
      const blob = await fetchDivaReportPdf(usageId);
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (error: any) {
      toast({
        title: "Error",
        description:
          error?.response?.data?.message || "Failed to load the report PDF",
        variant: "destructive",
      });
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleSaveResult = () => {
    const diva_scores: DivaScores = {};
    for (const { key } of SCORE_FIELDS) {
      const raw = scores[key];
      if (raw !== "" && raw !== undefined) {
        const n = Number(raw);
        if (Number.isInteger(n) && n >= 0 && n <= 9) diva_scores[key] = n;
      }
    }
    record(
      {
        usage_id: usageId,
        diva_outcome: outcome,
        ...(Object.keys(diva_scores).length ? { diva_scores } : {}),
      },
      { onSuccess: () => setOpen(false) },
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {reportLabel}
            {hasReport && <Badge variant="secondary">PDF saved</Badge>}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* ── PDF upload / manage ── */}
          <div className="space-y-2">
            <p className="text-sm font-medium">
              {hasReport
                ? "Clinician's report PDF"
                : "Upload the clinician's report PDF"}
            </p>
            {hasReport && (
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={togglePreview}
                  disabled={isLoadingPreview}
                  className="flex-1"
                >
                  {isLoadingPreview
                    ? "Loading…"
                    : previewUrl
                      ? "Hide saved PDF"
                      : "Preview saved PDF"}
                </Button>
              </div>
            )}
            {previewUrl && (
              <iframe
                src={previewUrl}
                title="Saved report PDF"
                className="w-full h-[55vh] border rounded-md"
              />
            )}
            <p className="text-xs text-muted-foreground">
              {hasReport
                ? "Upload a new file to replace the saved report."
                : "Saving parses the PDF and fills the result fields below."}
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              onChange={(e) => selectFile(e.target.files?.[0] ?? null)}
              className="block text-sm"
            />
            {filePreviewUrl && (
              <iframe
                src={filePreviewUrl}
                title="Selected report PDF"
                className="w-full h-[45vh] border rounded-md"
              />
            )}
            <Button
              onClick={handleUpload}
              disabled={!file || isSaving || isReparsing}
              className="w-full"
            >
              {isSaving || isReparsing
                ? "Uploading & parsing…"
                : hasReport
                  ? "Replace & re-parse PDF"
                  : "Upload & parse PDF"}
            </Button>
          </div>

          {/* ── Parsed result (only once a PDF exists) ── */}
          {hasReport && (
            <div className="space-y-4 border-t pt-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Diagnostic outcome
                </label>
                <Select value={outcome} onValueChange={setOutcome}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select the outcome" />
                  </SelectTrigger>
                  <SelectContent>
                    {outcomeOptions.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* IPDE has no per-domain x/9 scores — outcome only. */}
              {!isIpde && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    {SCORE_FIELDS.map(({ key, label }) => (
                      <div key={key} className="space-y-1">
                        <label className="text-xs text-muted-foreground">
                          {label}
                        </label>
                        <Input
                          type="number"
                          min={0}
                          max={9}
                          value={scores[key] ?? ""}
                          onChange={(e) =>
                            setScores({ ...scores, [key]: e.target.value })
                          }
                          placeholder="0–9"
                        />
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Auto-filled from the PDF; correct them here if needed. These
                    drive the score bars and the meets-DSM verdict on the
                    patient&apos;s report page.
                  </p>
                </>
              )}
              {isIpde && (
                <p className="text-xs text-muted-foreground">
                  Auto-parsed from the IPDE report. Correct here if the parse
                  got it wrong, then save.
                </p>
              )}
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleReparse}
                  disabled={isReparsing}
                  className="flex-1"
                >
                  {isReparsing ? "Re-parsing…" : "Re-parse from saved PDF"}
                </Button>
                <Button
                  onClick={handleSaveResult}
                  disabled={!outcome || isRecording}
                  className="flex-1"
                >
                  {isRecording ? "Saving…" : "Save DIVA result"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
